// API Configuration
// En desarrollo, Vite proxy redirige /api a localhost:5000
// En producción, /api está servido desde el mismo dominio
export const API_URL = import.meta.env.VITE_API_URL || ''
export const API_BASE_PATH = '/api'

// Helper para construir URLs de API
export const getApiUrl = (endpoint: string): string => {
  const base = API_URL || ''
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  return `${base}${API_BASE_PATH}${path}`
}

// App Configuration
export const APP_NAME = import.meta.env.VITE_APP_NAME || 'Sistema Mundi'
export const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0'

// Session Configuration
export const SESSION_TIMEOUT = Number(import.meta.env.VITE_SESSION_TIMEOUT) || 3600000 // 1 hour

// Pagination
export const DEFAULT_PAGE_SIZE = 20
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

// Currency - Issue #38: Expandido a 8 monedas de proveedores
export const DEFAULT_CURRENCY = 'USD'
export const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'Dólar Estadounidense' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GTQ', symbol: 'Q', name: 'Quetzal Guatemalteco' },
  { code: 'HNL', symbol: 'L', name: 'Lempira Hondureño' },
  { code: 'NIO', symbol: 'C$', name: 'Córdoba Nicaragüense' },
  { code: 'CRC', symbol: '₡', name: 'Colón Costarricense' },
  { code: 'PAB', symbol: 'B/.', name: 'Balboa Panameño' },
  { code: 'MXN', symbol: '$', name: 'Peso Mexicano' },
] as const

// Issue #38: Helper para generar opciones de select desde CURRENCIES
export const getCurrencyOptions = () =>
  CURRENCIES.map((c) => ({
    value: c.code,
    label: `${c.code} - ${c.name}`,
  }))

// Issue #38: Características de pricing de proveedores
export const PROVIDER_PRICING_FEATURES = {
  usa_paquetes: {
    label: 'Usa Paquetes (Bulk)',
    description: 'Códigos bulk con precios especiales por cantidad',
    example: 'Delco Remy: bulk qty, bulk code, bulk price',
  },
  escalonado_por_total: {
    label: 'Escalonado por Monto Total',
    description: 'Precios por nivel según monto total de cotización',
    example: 'Dana Spicer: Nivel 1-4 según total USD',
  },
  usa_multiples_listas: {
    label: 'Múltiples Listas de Precio',
    description: 'Listas separadas como CS (estándar) y FQ (especial)',
    example: 'Phinia: CS Price, FQ Price',
  },
  usa_precio_escalonado: {
    label: 'Precio Escalonado por Cantidad',
    description: 'Tiers de precio según cantidad del producto',
    example: 'qty_min: 1=100, 5=95, 10=90',
  },
} as const

// IVA
export const IVA_RATE = 0.16 // 16%

// Cotización States
export const COTIZACION_ESTADOS = {
  BORRADOR: 'borrador',
  ENVIADA: 'enviada',
  ACEPTADA: 'aceptada',
  RECHAZADA: 'rechazada',
  VENCIDA: 'vencida',
} as const

export const COTIZACION_ESTADOS_LABELS = {
  [COTIZACION_ESTADOS.BORRADOR]: 'Borrador',
  [COTIZACION_ESTADOS.ENVIADA]: 'Enviada',
  [COTIZACION_ESTADOS.ACEPTADA]: 'Aceptada',
  [COTIZACION_ESTADOS.RECHAZADA]: 'Rechazada',
  [COTIZACION_ESTADOS.VENCIDA]: 'Vencida',
}

// User Roles
export const USER_ROLES = {
  ADMIN: 'admin',
  USUARIO: 'usuario',
} as const

export const USER_ROLES_LABELS = {
  [USER_ROLES.ADMIN]: 'Administrador',
  [USER_ROLES.USUARIO]: 'Usuario',
}

// Date Formats
export const DATE_FORMAT_SHORT = 'dd/MM/yyyy'
export const DATE_FORMAT_LONG = 'dd MMMM yyyy'
export const DATETIME_FORMAT = 'dd/MM/yyyy HH:mm'

// Validez de cotización por defecto (días)
export const DEFAULT_VALIDEZ_DIAS = 30

// Condiciones de pago por defecto
export const DEFAULT_CONDICIONES_PAGO = 'Pago a 30 días'

// Tiempo de entrega por defecto
export const DEFAULT_TIEMPO_ENTREGA = '15 días hábiles'

// File upload
export const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
export const ACCEPTED_DOCUMENT_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']

// Toast Configuration
export const TOAST_DURATION = 4000
export const TOAST_SUCCESS_DURATION = 3000
export const TOAST_ERROR_DURATION = 5000

// Debounce delays
export const SEARCH_DEBOUNCE_DELAY = 300
export const INPUT_DEBOUNCE_DELAY = 500

// Chart Colors
export const CHART_COLORS = [
  '#0ea5e9', // primary
  '#22c55e', // success
  '#f59e0b', // warning
  '#ef4444', // danger
  '#a855f7', // purple
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
]

// Table
export const TABLE_ROW_HEIGHT = 48
export const TABLE_HEADER_HEIGHT = 40

// Breakpoints (matching Tailwind)
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH: 'auth-storage',
  THEME: 'theme-storage',
  SIDEBAR: 'sidebar-storage',
  FILTERS: 'filters-storage',
}

// Países Centroamérica (Issue #1)
export const PAISES = [
  { value: 'gt', label: 'Guatemala' },
  { value: 'sv', label: 'El Salvador' },
  { value: 'hn', label: 'Honduras' },
  { value: 'ni', label: 'Nicaragua' },
  { value: 'cr', label: 'Costa Rica' },
  { value: 'pa', label: 'Panamá' },
] as const;

// Formas de Pago (Issue #1)
export const FORMAS_PAGO = [
  { value: 'credito', label: 'Crédito' },
  { value: 'contado', label: 'Contado' },
] as const;
